import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";
import { motion, motionValue, useTransform, type MotionValue } from "framer-motion";
import { useScrollScrub } from "./useScrollScrub";

/**
 * Section-scoped parallax: one scroll driver per section, many items reading it.
 *
 * ── WHY A SHARED DRIVER, AND NOT ONE PER ELEMENT ──────────────────────────
 * This is the whole difference between depth and noise, so it is worth being
 * explicit about.
 *
 * If every card measures its OWN pass through the viewport, then every card
 * performs the identical motion, just offset in time by where it happens to
 * sit. Nothing is nearer or further than anything else — the page simply
 * jitters, each element repeating its neighbour's gesture a moment later. That
 * is the generic scroll-effect look, and adding more of it makes it worse.
 *
 * A shared driver inverts that. Every item in a section reads the SAME progress
 * value and differs only by `depth`, so at any instant the near things have
 * genuinely moved further than the far things. That is what the eye reads as
 * space rather than as activity.
 *
 * It is also cheaper: one `useScroll` and one spring per section instead of one
 * per element.
 * ──────────────────────────────────────────────────────────────────────────
 *
 * Travel is a fixed distance scaled by depth, NOT a percentage of each
 * element's own size — a tall card and a short chip at the same depth have to
 * move the same distance or they read as being at different depths.
 */

const SceneContext = createContext<MotionValue<number> | null>(null);

/** Parked value so hook order never changes on the inert branch. */
const MOTIONLESS = motionValue(0);

/** Full travel at depth 1.0, in px. Depths are all well under 1. */
const SCENE_TRAVEL = 200;

/** Below this, travel halves — the standing constraint for small screens. */
const COMPACT_QUERY = "(max-width: 767px)";

/**
 * The depth grammar. Assignments are by ROLE, not by taste, so a section built
 * later lands in the same space as the ones built already.
 *
 * The ordering carries an argument: what you are meant to read first sits
 * nearest and therefore moves most, and decoration sits furthest and lags.
 * `base` is exactly zero and it is where running body copy belongs — text that
 * drifts against its own container is harder to read and is the single fastest
 * way to make a page feel cheap.
 */
export const DEPTH = {
  /** Washes, dot fields, glows. Lags the page. */
  back: -0.1,
  /** Large media and panels — present, but behind the reading plane. */
  mid: -0.05,
  /** Body copy. Does not move. */
  base: 0,
  /** Cards, chips, list rows. */
  near: 0.06,
  /** Headings and eyebrows — the first thing read, so the nearest thing. */
  fore: 0.11,
} as const;

/**
 * Fans a row of siblings so they do not travel as one slab.
 *
 * The increment is deliberately tiny. Enough that the row has an internal
 * rake; not so much that the third card reads as belonging to a different
 * plane than the first.
 */
export function fanDepth(base: number, index: number): number {
  return base + index * 0.012;
}

function useCompact(): boolean {
  const [compact, setCompact] = useState(
    () => typeof window !== "undefined" && window.matchMedia(COMPACT_QUERY).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(COMPACT_QUERY);
    const sync = (e: MediaQueryListEvent) => setCompact(e.matches);
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  return compact;
}

/**
 * Publishes a section's scroll progress to every `ParallaxItem` inside it.
 *
 * Takes a ref to an element that already exists rather than rendering a wrapper
 * of its own: a new div here would land between a grid and its children and
 * silently break every layout it was added to.
 */
export function ParallaxScene({
  target,
  children,
}: {
  target: RefObject<HTMLElement | null>;
  children: ReactNode;
}) {
  const scrub = useScrollScrub(target);
  return <SceneContext.Provider value={scrub}>{children}</SceneContext.Provider>;
}

export interface ParallaxItemProps {
  depth?: number;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
  "aria-hidden"?: boolean | "true" | "false";
}

/**
 * One element travelling at its own depth inside a scene.
 *
 * Renders a plain div when there is no scene, when the route's motion level is
 * below "loud", and under `prefers-reduced-motion` — no MotionValue bound to
 * the DOM, no `will-change`, no transform. Content is never hidden by this
 * component in any state; it only ever offsets something already visible.
 */
export function ParallaxItem({
  depth = DEPTH.near,
  className,
  style,
  children,
  ...rest
}: ParallaxItemProps) {
  const scene = useContext(SceneContext);
  const compact = useCompact();

  const travel = depth * SCENE_TRAVEL * (compact ? 0.5 : 1);
  const y = useTransform(scene ?? MOTIONLESS, [0, 1], [travel, -travel]);

  if (!scene) {
    return (
      <div className={className} style={style} {...rest}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      className={className}
      style={{ ...style, y, willChange: "transform" }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

/**
 * A standalone decorative layer that drives itself.
 *
 * For layers that sit OUTSIDE any scene — the hero's washes, for instance,
 * where there is no section-level scrub to share. Inside a section, prefer
 * `ParallaxItem`: it costs nothing extra and moves in concert with its
 * neighbours instead of independently.
 *
 * Travel here is a percentage of the layer's own height, which suits large
 * washes whose size is the point.
 */
export function ParallaxLayer({
  depth = 0.1,
  className,
  style,
  children,
  ...rest
}: ParallaxItemProps) {
  const ref = useRef<HTMLDivElement>(null);
  const scrub = useScrollScrub(ref);
  const compact = useCompact();

  const amplitude = depth * 50 * (compact ? 0.5 : 1);
  const y = useTransform(
    scrub ?? MOTIONLESS,
    [0, 1],
    [`${amplitude}%`, `${-amplitude}%`],
  );

  if (!scrub) {
    return (
      <div ref={ref} className={className} style={style} {...rest}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      ref={ref}
      className={className}
      style={{ ...style, y, willChange: "transform" }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}
